import type { Task, SavedTask } from '@/lib/types'
import { db, generateSyncId } from './database'
import { getToken } from './googleDrive'

// ─── Constants ──────────────────────────────────────
const TASK_LIST_ID = '@default'
const SYNC_INTERVAL_MS = 60_000 // 60 seconds
const PUSH_DEBOUNCE_MS = 300
const MAX_WRITES_PER_SYNC = 30    // Google limit: 500/100s ≈ 5/s, 우리는 2/s × 60s = 충분
const WRITE_DELAY_MS = 500        // 500ms = 2 req/s (한계치 5/s의 40%)
const ACTIVE_WINDOW_DAYS = 30

// ─── State ──────────────────────────────────────────
let syncInterval: ReturnType<typeof setInterval> | null = null
let isSyncing = false
let lastSyncTime: string | null = null
const pushTimers = new Map<number, ReturnType<typeof setTimeout>>()

// Loop prevention: tracks syncIds recently pushed to Google to avoid re-pull on next poll
export const recentlyPushedToGoogle = new Set<string>()
function markPushedToGoogle(syncId: string) {
  recentlyPushedToGoogle.add(syncId)
  setTimeout(() => recentlyPushedToGoogle.delete(syncId), 10_000)
}

// Quota cooldown: stop all Google API writes for 60s after quotaExceeded
let quotaCooldownUntil = 0
function isQuotaCoolingDown(): boolean {
  return Date.now() < quotaCooldownUntil
}
function activateQuotaCooldown(): void {
  quotaCooldownUntil = Date.now() + 60_000 // #8: 60초로 증가 (30초는 quota 미회복)
  console.warn('[GoogleTasks] 할당량 쿨다운 활성화 — 60초 동안 push 중단')
}

// #9: 중앙화된 quota 에러 감지 — 문자열 매칭 + HTTP 상태 코드
function isQuotaError(err: any): boolean {
  const status = err?.status || err?.result?.error?.code
  if (status === 429) return true
  const msg = String(err?.result?.error?.message || err?.message || '').toLowerCase()
  return msg.includes('quotaexceeded') || msg.includes('rate limit') || msg.includes('rate_limit')
}

// ─── Active Window Filter ────────────────────────────
function isInActiveWindow(task: {
  status?: string
  completedAt?: string
  updatedAt?: string
  updated?: string
}): boolean {
  if (task.status === 'pending' || task.status === 'needsAction') return true

  const cutoff = new Date()
  cutoff.setDate(cutoff.getDate() - ACTIVE_WINDOW_DAYS)
  const cutoffTime = cutoff.getTime()

  if (task.completedAt && new Date(task.completedAt).getTime() >= cutoffTime) return true
  if (task.updated && new Date(task.updated).getTime() >= cutoffTime) return true
  if (task.updatedAt && new Date(task.updatedAt).getTime() >= cutoffTime) return true

  return false
}

// ─── Firestore Bridge ───────────────────────────────

async function bridgePushToFirestore(task: Task): Promise<void> {
  if (!task.syncId) return
  try {
    const { pushTask } = await import('./firestoreSync')
    await pushTask(task)
  } catch (err) {
    console.warn('[GoogleTasks] Firestore bridge push 실패:', err)
  }
}

async function bridgeDeleteFromFirestore(syncId: string): Promise<void> {
  try {
    const { deleteTaskFromCloud, isSyncActive: isCloudSyncActive } = await import('./firestoreSync')
    if (!isCloudSyncActive()) {
      console.warn('[GoogleTasks] Firestore sync 비활성 — 삭제 건너뜀:', syncId)
      return
    }
    await deleteTaskFromCloud(syncId)
  } catch (err) {
    console.warn('[GoogleTasks] Firestore bridge delete 실패:', err)
    throw err // deduplicateLocalTasks의 Promise.allSettled에서 감지하도록 재throw
  }
}

// ─── Types ──────────────────────────────────────────
export interface SyncResult {
  pushed: number
  pulled: number
  deleted: number
  errors: string[]
}

interface GoogleTask {
  id: string
  title: string
  notes?: string
  status: 'needsAction' | 'completed'
  due?: string
  completed?: string
  updated: string
  deleted?: boolean
}

// ─── Field Mapping ──────────────────────────────────

function googleTaskToLocalFields(gt: GoogleTask): Partial<Task> {
  const fields: Partial<Task> = {
    title: gt.title || '(제목 없음)',
    memo: gt.notes || '',
    status: gt.status === 'completed' ? 'completed' : 'pending',
    googleTaskId: gt.id,
    googleTaskUpdatedAt: gt.updated,
  }

  if (gt.due) {
    // Google Tasks due is RFC 3339 date — extract YYYY-MM-DD
    fields.dueDate = gt.due.split('T')[0]
  } else {
    fields.dueDate = undefined
  }

  if (gt.status === 'completed' && gt.completed) {
    fields.completedAt = gt.completed
  } else if (gt.status === 'needsAction') {
    fields.completedAt = undefined
  }

  return fields
}

function localTaskToGoogleResource(task: Task): Record<string, unknown> {
  const resource: Record<string, unknown> = {
    title: task.title,
    notes: task.memo || '',
    status: task.status === 'completed' ? 'completed' : 'needsAction',
  }

  if (task.dueDate) {
    resource.due = `${task.dueDate}T00:00:00.000Z`
  }

  if (task.status === 'completed' && task.completedAt) {
    resource.completed = task.completedAt
  }

  return resource
}

// ─── Google Tasks API Wrappers ──────────────────────

async function fetchAllGoogleTasks(): Promise<GoogleTask[]> {
  const allTasks: GoogleTask[] = []
  let pageToken: string | undefined

  do {
    const params: Record<string, unknown> = {
      tasklist: TASK_LIST_ID,
      maxResults: 100,
      showCompleted: true,
      showDeleted: true,
      showHidden: true,
    }
    if (pageToken) params.pageToken = pageToken

    const response = await window.gapi.client.tasks.tasks.list(params)
    const items = response.result?.items as GoogleTask[] | undefined
    if (items) allTasks.push(...items)
    pageToken = response.result?.nextPageToken
  } while (pageToken)

  return allTasks
}

async function fetchUpdatedGoogleTasks(updatedMin: string): Promise<GoogleTask[]> {
  const allTasks: GoogleTask[] = []
  let pageToken: string | undefined

  do {
    const params: Record<string, unknown> = {
      tasklist: TASK_LIST_ID,
      maxResults: 100,
      showCompleted: true,
      showDeleted: true,
      showHidden: true,
      updatedMin,
    }
    if (pageToken) params.pageToken = pageToken

    const response = await window.gapi.client.tasks.tasks.list(params)
    const items = response.result?.items as GoogleTask[] | undefined
    if (items) allTasks.push(...items)
    pageToken = response.result?.nextPageToken
  } while (pageToken)

  return allTasks
}

async function insertGoogleTask(resource: Record<string, unknown>): Promise<GoogleTask> {
  const response = await window.gapi.client.tasks.tasks.insert({
    tasklist: TASK_LIST_ID,
    resource,
  })
  return response.result as GoogleTask
}

async function updateGoogleTask(taskId: string, resource: Record<string, unknown>): Promise<GoogleTask> {
  const response = await window.gapi.client.tasks.tasks.update({
    tasklist: TASK_LIST_ID,
    task: taskId,
    resource: { id: taskId, ...resource },
  })
  return response.result as GoogleTask
}

async function deleteGoogleTask(taskId: string): Promise<void> {
  await window.gapi.client.tasks.tasks.delete({
    tasklist: TASK_LIST_ID,
    task: taskId,
  })
}

// ─── Core Sync Logic ────────────────────────────────

export async function fullSync(): Promise<SyncResult> {
  if (!getToken()) {
    console.warn('[GoogleTasks] fullSync 중단: 인증 토큰 없음')
    return { pushed: 0, pulled: 0, deleted: 0, errors: ['인증 토큰 없음'] }
  }
  if (!window.gapi?.client?.tasks) {
    console.warn('[GoogleTasks] fullSync 중단: Tasks API 미초기화 — gapi.client.tasks 없음')
    return { pushed: 0, pulled: 0, deleted: 0, errors: ['Tasks API 미초기화'] }
  }
  console.log('[GoogleTasks] fullSync 시작...')

  const result: SyncResult = { pushed: 0, pulled: 0, deleted: 0, errors: [] }

  try {
    const [googleTasks, localTasks] = await Promise.all([
      fetchAllGoogleTasks(),
      db.tasks.toArray() as Promise<SavedTask[]>,
    ])

    // Active window filter: 처리 대상은 활성 task만, googleMap은 전체로 유지 (orphan 감지용)
    const activeGoogleTasks = googleTasks.filter(gt => {
      if (gt.deleted) return true
      return isInActiveWindow({
        status: gt.status === 'completed' ? 'completed' : 'pending',
        completedAt: gt.completed,
        updated: gt.updated,
      })
    })

    // Build lookup maps (전체 googleTasks로 빌드 — orphan 감지 정확성 보장)
    const googleMap = new Map<string, GoogleTask>()
    for (const gt of googleTasks) googleMap.set(gt.id, gt)

    const localByGoogleId = new Map<string, SavedTask>()
    for (const lt of localTasks) {
      if (lt.googleTaskId) localByGoogleId.set(lt.googleTaskId, lt)
    }

    // Title-based dedup map for unlinked local tasks (prevents duplicates on reconnect)
    // 배열로 관리하여 같은 제목의 task가 여러 개일 때도 정확히 매칭
    const unlinkedLocalByTitle = new Map<string, SavedTask[]>()
    for (const lt of localTasks) {
      if (!lt.googleTaskId && lt.title) {
        const key = lt.title.trim().toLowerCase()
        const arr = unlinkedLocalByTitle.get(key) || []
        arr.push(lt)
        unlinkedLocalByTitle.set(key, arr)
      }
    }

    // Track which local tasks got linked during Google→Local pass
    const newlyLinkedLocalIds = new Set<number>()

    // Global write budget — limits total Google API writes per sync cycle
    let writesRemaining = MAX_WRITES_PER_SYNC
    let quotaHit = false

    async function throttledWrite(): Promise<boolean> {
      if (quotaHit || writesRemaining <= 0) return false
      if (writesRemaining < MAX_WRITES_PER_SYNC) {
        await new Promise((r) => setTimeout(r, WRITE_DELAY_MS))
      }
      return true
    }

    // 1. Process Google Tasks (활성 창 내 task만)
    for (const gt of activeGoogleTasks) {
      try {
        const localTask = localByGoogleId.get(gt.id)

        if (gt.deleted) {
          // Google task deleted → delete from Firestore + local
          if (localTask) {
            if (localTask.syncId) await bridgeDeleteFromFirestore(localTask.syncId)
            await db.tasks.delete(localTask.id)
            result.deleted++
          }
          continue
        }

        if (localTask) {
          // Both exist — compare timestamps for conflict resolution
          const googleUpdated = new Date(gt.updated).getTime()
          const lastKnown = localTask.googleTaskUpdatedAt
            ? new Date(localTask.googleTaskUpdatedAt).getTime()
            : 0

          if (googleUpdated > lastKnown) {
            // Google is newer — update local + push to Firestore
            const updates = googleTaskToLocalFields(gt)
            await db.tasks.update(localTask.id, {
              ...updates,
              updatedAt: gt.updated, // Google 타임스탬프 보존 — 불필요한 전파 방지
            })
            const updatedTask = await db.tasks.get(localTask.id) as SavedTask
            if (updatedTask) await bridgePushToFirestore(updatedTask)
            result.pulled++
          } else if (!quotaHit) {
            // Local is newer or same — push to Google (budget-limited)
            const localUpdated = new Date(localTask.updatedAt).getTime()
            if (localUpdated > lastKnown) {
              if (!(await throttledWrite())) continue
              try {
                const resource = localTaskToGoogleResource(localTask)
                const updated = await updateGoogleTask(gt.id, resource)
                await db.tasks.update(localTask.id, {
                  googleTaskUpdatedAt: updated.updated,
                })
                writesRemaining--
                result.pushed++
              } catch (writeErr: any) {
                if (isQuotaError(writeErr)) {
                  quotaHit = true
                  activateQuotaCooldown()
                  console.warn(`[GoogleTasks] Section 1 할당량 초과 — update 중단`)
                } else {
                  throw writeErr
                }
              }
            }
          }
        } else {
          // Google task exists, no local match by googleTaskId
          // → Try title-based matching to prevent duplicates on reconnect
          const titleKey = (gt.title || '').trim().toLowerCase()
          const candidates = titleKey ? unlinkedLocalByTitle.get(titleKey) : undefined

          if (candidates && candidates.length > 0) {
            // dueDate + status로 best match 선택, 없으면 첫 번째
            const gtDueDate = gt.due ? gt.due.split('T')[0] : undefined
            const gtStatus = gt.status === 'completed' ? 'completed' : 'pending'
            const titleMatch = candidates.find(c =>
              c.dueDate === gtDueDate && c.status === gtStatus
            ) || candidates[0]

            // 매칭된 task를 후보에서 제거
            const idx = candidates.indexOf(titleMatch)
            if (idx !== -1) candidates.splice(idx, 1)
            if (candidates.length === 0) unlinkedLocalByTitle.delete(titleKey)

            // Link existing local task to this Google task instead of creating duplicate
            console.log(`[GoogleTasks] 제목 매칭으로 연결: "${gt.title}" (local #${titleMatch.id} ↔ google ${gt.id})`)
            const updates = googleTaskToLocalFields(gt)
            await db.tasks.update(titleMatch.id, {
              ...updates,
              googleTaskId: gt.id,
              googleTaskListId: TASK_LIST_ID,
              googleTaskUpdatedAt: gt.updated,
              updatedAt: gt.updated,
            })
            newlyLinkedLocalIds.add(titleMatch.id)
            const updatedTask = await db.tasks.get(titleMatch.id) as SavedTask
            if (updatedTask) await bridgePushToFirestore(updatedTask)
            result.pulled++
          } else if (titleKey && localTasks.some(t => t.googleTaskId && t.googleTaskId !== gt.id && t.title?.trim().toLowerCase() === titleKey)) {
            // A local task with same title is already linked to a DIFFERENT Google task
            // → This Google task is an orphaned duplicate — delete it from Google
            console.log(`[GoogleTasks] 중복 Google task 삭제: "${gt.title}" (${gt.id}) — 이미 다른 Google ID로 연결된 로컬 task 존재`)
            if (await throttledWrite()) {
              try {
                await deleteGoogleTask(gt.id)
                writesRemaining--
                result.deleted++
              } catch (delErr: any) {
                if (delErr?.status !== 404 && delErr?.result?.error?.code !== 404) {
                  if (isQuotaError(delErr)) {
                    quotaHit = true
                    activateQuotaCooldown()
                  }
                }
              }
            }
          } else {
            // No match — create new local task
            const now = new Date().toISOString()
            const fields = googleTaskToLocalFields(gt)
            const newTask: Omit<Task, 'id'> = {
              title: fields.title || '(제목 없음)',
              categoryId: null,
              status: fields.status || 'pending',
              priority: 'none',
              isFlagged: false,
              isStarred: false,
              dueDate: fields.dueDate,
              alarm: { enabled: false },
              repeat: { type: 'none', interval: 0 },
              memo: fields.memo || '',
              subtasks: [],
              completedAt: fields.completedAt,
              createdAt: now,
              updatedAt: now,
              sortOrder: 0,
              syncId: generateSyncId(),
              googleTaskId: gt.id,
              googleTaskListId: TASK_LIST_ID,
              googleTaskUpdatedAt: gt.updated,
            }
            const addedId = await db.tasks.add(newTask as Task)
            const addedTask = await db.tasks.get(addedId) as SavedTask
            if (addedTask) await bridgePushToFirestore(addedTask)
            result.pulled++
          }
        }
      } catch (err) {
        result.errors.push(`Google Task 처리 실패 (${gt.id}): ${err instanceof Error ? err.message : String(err)}`)
      }
    }

    // Build title set of existing Google tasks for reverse dedup
    const googleTitleSet = new Set<string>()
    for (const gt of googleTasks) {
      if (!gt.deleted && gt.title) googleTitleSet.add(gt.title.trim().toLowerCase())
    }

    // 2. Push local tasks that have no googleTaskId (budget-limited)
    // Skip tasks that were just linked above or whose title already exists in Google
    const unlinkedTasks = localTasks.filter((t) =>
      !t.googleTaskId &&
      !newlyLinkedLocalIds.has(t.id) &&
      !(t.title && googleTitleSet.has(t.title.trim().toLowerCase())) &&
      isInActiveWindow(t)
    )

    if (!quotaHit && writesRemaining > 0 && unlinkedTasks.length > 0) {
      const pushBatch = unlinkedTasks.slice(0, writesRemaining)
      console.log(`[GoogleTasks] ${unlinkedTasks.length}개 미연결 task 중 ${pushBatch.length}개 → Google push (budget: ${writesRemaining})`)

      for (const task of pushBatch) {
        if (quotaHit || writesRemaining <= 0) break
        try {
          await new Promise((r) => setTimeout(r, WRITE_DELAY_MS))
          const resource = localTaskToGoogleResource(task)
          const created = await insertGoogleTask(resource)
          await db.tasks.update(task.id, {
            googleTaskId: created.id,
            googleTaskListId: TASK_LIST_ID,
            googleTaskUpdatedAt: created.updated,
          })
          const updatedTask = await db.tasks.get(task.id) as SavedTask
          if (updatedTask) await bridgePushToFirestore(updatedTask)
          writesRemaining--
          result.pushed++
        } catch (err: any) {
          const errMsg = err?.result?.error?.message || (err instanceof Error ? err.message : String(err))
          if (isQuotaError(err)) {
            quotaHit = true
            activateQuotaCooldown()
            console.warn(`[GoogleTasks] 할당량 초과 — ${result.pushed}개 push 완료, 나머지는 다음 동기화에서 처리`)
            result.errors.push(`할당량 초과: ${result.pushed}개 push 후 중단`)
            break
          }
          result.errors.push(`로컬 Task 푸시 실패 (${task.id}): ${errMsg}`)
        }
      }
    }
    const remainingUnlinked = unlinkedTasks.length - Math.min(unlinkedTasks.length, writesRemaining > 0 ? unlinkedTasks.length : 0)
    if (remainingUnlinked > 0) {
      console.log(`[GoogleTasks] ${remainingUnlinked}개 task는 다음 동기화에서 처리`)
    }

    // 3. Handle local tasks whose googleTaskId no longer exists in Google
    for (const task of localTasks) {
      if (task.googleTaskId && !googleMap.has(task.googleTaskId) && isInActiveWindow(task)) {
        // Google side deleted — remove googleTaskId from local
        await db.tasks.update(task.id, {
          googleTaskId: undefined,
          googleTaskListId: undefined,
          googleTaskUpdatedAt: undefined,
        })
        result.deleted++
      }
    }

    // Update last sync time
    lastSyncTime = new Date().toISOString()
    const { useSettingsStore } = await import('@/stores/settingsStore')
    useSettingsStore.getState().setGoogleTasksLastSync(lastSyncTime)

    // Refresh taskStore from DB
    const { useTaskStore } = await import('@/stores/taskStore')
    await useTaskStore.getState().refreshFromDb()

    const skipped = googleTasks.length - activeGoogleTasks.length
    console.log(`[GoogleTasks] fullSync 완료 — pulled: ${result.pulled}, pushed: ${result.pushed}, deleted: ${result.deleted}, skipped(창 밖): ${skipped}, errors: ${result.errors.length}`)
  } catch (err) {
    const msg = err instanceof Error ? err.message : String(err)
    result.errors.push(`Full sync 실패: ${msg}`)
    console.error('[GoogleTasks] fullSync 실패:', err)

    // Handle auth errors (but not quota errors)
    if (!isQuotaError(err) && (msg.includes('401') || msg.includes('403'))) {
      stopGoogleTasksSync()
    }
  }

  return result
}

export async function incrementalSync(): Promise<SyncResult> {
  if (!lastSyncTime) {
    console.log('[GoogleTasks] incrementalSync → lastSyncTime 없음, fullSync로 전환')
    return fullSync()
  }
  if (!getToken()) {
    console.warn('[GoogleTasks] incrementalSync 중단: 인증 토큰 없음')
    return { pushed: 0, pulled: 0, deleted: 0, errors: ['인증 토큰 없음'] }
  }
  if (!window.gapi?.client?.tasks) {
    console.warn('[GoogleTasks] incrementalSync 중단: Tasks API 미초기화')
    return { pushed: 0, pulled: 0, deleted: 0, errors: ['Tasks API 미초기화'] }
  }

  const result: SyncResult = { pushed: 0, pulled: 0, deleted: 0, errors: [] }

  try {
    const googleTasks = await fetchUpdatedGoogleTasks(lastSyncTime)

    // Active window filter
    const activeGoogleTasks = googleTasks.filter(gt => {
      if (gt.deleted) return true
      return isInActiveWindow({
        status: gt.status === 'completed' ? 'completed' : 'pending',
        completedAt: gt.completed,
        updated: gt.updated,
      })
    })

    // #4: 루프 밖에서 1번만 로드 (성능 최적화)
    const allLocalTasks = await db.tasks.toArray() as SavedTask[]

    for (const gt of activeGoogleTasks) {
      try {
        const localTask = await db.tasks.where('googleTaskId').equals(gt.id).first() as SavedTask | undefined

        if (gt.deleted) {
          if (localTask) {
            if (localTask.syncId) await bridgeDeleteFromFirestore(localTask.syncId)
            await db.tasks.delete(localTask.id)
            result.deleted++
          }
          continue
        }

        if (localTask) {
          const googleUpdated = new Date(gt.updated).getTime()
          const lastKnown = localTask.googleTaskUpdatedAt
            ? new Date(localTask.googleTaskUpdatedAt).getTime()
            : 0

          if (googleUpdated > lastKnown) {
            const updates = googleTaskToLocalFields(gt)
            await db.tasks.update(localTask.id, {
              ...updates,
              updatedAt: gt.updated, // Google 타임스탬프 보존 — 불필요한 전파 방지
            })
            const updatedTask = await db.tasks.get(localTask.id) as SavedTask
            if (updatedTask) await bridgePushToFirestore(updatedTask)
            result.pulled++
          }
        } else {
          // New Google task — try title-based matching first (prevents duplicates from Firestore listener)
          const titleKey = (gt.title || '').trim().toLowerCase()
          const gtDueDate = gt.due ? gt.due.split('T')[0] : undefined
          const gtStatus = gt.status === 'completed' ? 'completed' : 'pending'
          // dueDate+status까지 매칭 시도 → 실패 시 제목만으로 fallback
          const titleMatch = titleKey
            ? allLocalTasks.find(t =>
                !t.googleTaskId && t.title && t.title.trim().toLowerCase() === titleKey
                && t.dueDate === gtDueDate && t.status === gtStatus
              ) || allLocalTasks.find(t =>
                !t.googleTaskId && t.title && t.title.trim().toLowerCase() === titleKey
              )
            : undefined

          if (titleMatch) {
            // Link existing local task to this Google task
            console.log(`[GoogleTasks] incremental 제목 매칭: "${gt.title}" (local #${titleMatch.id} ↔ google ${gt.id})`)
            const updates = googleTaskToLocalFields(gt)
            await db.tasks.update(titleMatch.id, {
              ...updates,
              googleTaskId: gt.id,
              googleTaskListId: TASK_LIST_ID,
              googleTaskUpdatedAt: gt.updated,
              updatedAt: gt.updated, // Google 타임스탬프 보존
            })
            const updatedTask = await db.tasks.get(titleMatch.id) as SavedTask
            if (updatedTask) await bridgePushToFirestore(updatedTask)
            result.pulled++
          } else if (titleKey) {
            // Check if a local task with same title is already linked to a different Google task
            const linkedDuplicate = allLocalTasks.find(t =>
              t.googleTaskId && t.googleTaskId !== gt.id && t.title?.trim().toLowerCase() === titleKey
            )
            if (linkedDuplicate) {
              // Orphaned duplicate Google task — delete from Google
              console.log(`[GoogleTasks] incremental 중복 Google task 삭제: "${gt.title}" (${gt.id})`)
              try {
                await deleteGoogleTask(gt.id)
                result.deleted++
              } catch (delErr: any) {
                if (delErr?.status !== 404 && delErr?.result?.error?.code !== 404) {
                  console.warn('[GoogleTasks] 중복 삭제 실패:', delErr)
                }
              }
              continue
            }
            // No match — create new local task
            const now = new Date().toISOString()
            const fields = googleTaskToLocalFields(gt)
            const newTask: Omit<Task, 'id'> = {
              title: fields.title || '(제목 없음)',
              categoryId: null,
              status: fields.status || 'pending',
              priority: 'none',
              isFlagged: false,
              isStarred: false,
              dueDate: fields.dueDate,
              alarm: { enabled: false },
              repeat: { type: 'none', interval: 0 },
              memo: fields.memo || '',
              subtasks: [],
              completedAt: fields.completedAt,
              createdAt: now,
              updatedAt: now,
              sortOrder: 0,
              syncId: generateSyncId(),
              googleTaskId: gt.id,
              googleTaskListId: TASK_LIST_ID,
              googleTaskUpdatedAt: gt.updated,
            }
            const addedId = await db.tasks.add(newTask as Task)
            const addedTask = await db.tasks.get(addedId) as SavedTask
            if (addedTask) await bridgePushToFirestore(addedTask)
            result.pulled++
          }
        }
      } catch (err) {
        result.errors.push(`Incremental sync task 처리 실패 (${gt.id}): ${err instanceof Error ? err.message : String(err)}`)
      }
    }

    // Update last sync time
    lastSyncTime = new Date().toISOString()
    const { useSettingsStore } = await import('@/stores/settingsStore')
    useSettingsStore.getState().setGoogleTasksLastSync(lastSyncTime)

    // Refresh taskStore if anything changed
    if (result.pulled > 0 || result.deleted > 0) {
      const { useTaskStore } = await import('@/stores/taskStore')
      await useTaskStore.getState().refreshFromDb()
    }

    if (result.pulled > 0 || result.pushed > 0 || result.deleted > 0) {
      console.log(`[GoogleTasks] incrementalSync 완료 — pulled: ${result.pulled}, pushed: ${result.pushed}, deleted: ${result.deleted}`)
    }
  } catch (err) {
    const msg = err instanceof Error ? err.message : String(err)
    result.errors.push(`Incremental sync 실패: ${msg}`)
    console.error('[GoogleTasks] incrementalSync 실패:', err)

    if (!isQuotaError(err) && (msg.includes('401') || msg.includes('403'))) {
      stopGoogleTasksSync()
    }
  }

  return result
}

// ─── Push Operations ────────────────────────────────

export async function pushTaskToGoogle(task: SavedTask): Promise<void> {
  if (!getToken() || !window.gapi?.client?.tasks) return
  if (isQuotaCoolingDown()) return

  try {
    const resource = localTaskToGoogleResource(task)

    if (task.googleTaskId) {
      // Update existing
      const updated = await updateGoogleTask(task.googleTaskId, resource)
      await db.tasks.update(task.id, {
        googleTaskUpdatedAt: updated.updated,
      })
      // Firestore에 최신 상태 동기화
      const freshTask = await db.tasks.get(task.id) as SavedTask
      if (freshTask) await bridgePushToFirestore(freshTask)
    } else {
      // Create new
      const created = await insertGoogleTask(resource)
      await db.tasks.update(task.id, {
        googleTaskId: created.id,
        googleTaskListId: TASK_LIST_ID,
        googleTaskUpdatedAt: created.updated,
      })
      // Firestore에 googleTaskId 동기화
      const freshTask = await db.tasks.get(task.id) as SavedTask
      if (freshTask) await bridgePushToFirestore(freshTask)
    }
    // Mark after successful push — prevents re-pull on next poll
    if (task.syncId) markPushedToGoogle(task.syncId)
  } catch (err: any) {
    // 404 means the task was deleted on Google side
    if (err?.status === 404 || err?.result?.error?.code === 404) {
      await db.tasks.update(task.id, {
        googleTaskId: undefined,
        googleTaskListId: undefined,
        googleTaskUpdatedAt: undefined,
      })
      // Re-create on Google
      try {
        const resource = localTaskToGoogleResource(task)
        const created = await insertGoogleTask(resource)
        await db.tasks.update(task.id, {
          googleTaskId: created.id,
          googleTaskListId: TASK_LIST_ID,
          googleTaskUpdatedAt: created.updated,
        })
        if (task.syncId) markPushedToGoogle(task.syncId)
        // 재생성 후 Firestore 동기화
        const freshTask = await db.tasks.get(task.id) as SavedTask
        if (freshTask) await bridgePushToFirestore(freshTask)
      } catch {
        // Silently fail — will retry on next sync
      }
    } else {
      if (isQuotaError(err)) {
        activateQuotaCooldown()
      } else {
        console.error('[GoogleTasks] push 실패:', err)
      }
    }
  }
}

export function debouncedPushGoogleTask(task: SavedTask): void {
  const existing = pushTimers.get(task.id)
  if (existing) clearTimeout(existing)
  pushTimers.set(
    task.id,
    setTimeout(() => {
      pushTimers.delete(task.id)
      pushTaskToGoogle(task).catch(console.error)
    }, PUSH_DEBOUNCE_MS)
  )
}

export async function deleteTaskFromGoogle(googleTaskId: string): Promise<void> {
  if (!getToken() || !window.gapi?.client?.tasks) return
  if (isQuotaCoolingDown()) return

  try {
    await deleteGoogleTask(googleTaskId)
  } catch (err: any) {
    // 404 is fine — already deleted
    if (err?.status !== 404 && err?.result?.error?.code !== 404) {
      console.error('Google Tasks delete failed:', err)
    }
  }
}

// ─── Polling Lifecycle ──────────────────────────────

async function runSync(): Promise<void> {
  if (isSyncing) return
  isSyncing = true
  try {
    await incrementalSync()
  } finally {
    isSyncing = false
  }
}

function handleVisibilityChange(): void {
  if (document.visibilityState === 'visible') {
    // Tab became visible — run sync immediately and restart interval
    runSync().catch(console.error) // #14: unhandled rejection 방지
    if (syncInterval) clearInterval(syncInterval)
    syncInterval = setInterval(runSync, SYNC_INTERVAL_MS)
  } else {
    // Tab hidden — pause polling
    if (syncInterval) {
      clearInterval(syncInterval)
      syncInterval = null
    }
  }
}

export function startGoogleTasksSync(): void {
  if (syncInterval) {
    console.log('[GoogleTasks] sync 이미 실행 중 — 건너뜀')
    return
  }
  if (!getToken()) {
    console.warn('[GoogleTasks] sync 시작 불가: 토큰 없음')
    return
  }

  console.log('[GoogleTasks] sync 시작 — 60초 간격 폴링')

  // Initial sync
  fullSync().catch(console.error)

  // Start polling
  syncInterval = setInterval(runSync, SYNC_INTERVAL_MS)

  // Visibility-based polling
  document.addEventListener('visibilitychange', handleVisibilityChange)
}

export function stopGoogleTasksSync(): void {
  if (syncInterval) {
    clearInterval(syncInterval)
    syncInterval = null
  }
  document.removeEventListener('visibilitychange', handleVisibilityChange)

  // Clear pending push timers
  for (const timer of pushTimers.values()) clearTimeout(timer)
  pushTimers.clear()
}

// ─── Status Queries ─────────────────────────────────

export function isSyncActive(): boolean {
  return syncInterval !== null
}

export function getLastSyncTime(): string | null {
  return lastSyncTime
}

export function isGoogleTasksSyncActive(): boolean {
  return syncInterval !== null && !!getToken()
}

// ─── Deduplicate Local Tasks ────────────────────────

export async function deduplicateLocalTasks(): Promise<number> {
  const allTasks = await db.tasks.toArray() as SavedTask[]
  const seen = new Map<string, SavedTask>()
  const toDelete: number[] = []

  for (const task of allTasks) {
    // Key: title + dueDate (or 'no-date') + status
    const key = `${(task.title || '').trim().toLowerCase()}|${task.dueDate || 'no-date'}|${task.status}`
    const existing = seen.get(key)
    if (existing) {
      // Keep the one with googleTaskId, or the older one (lower id = created first)
      if (!task.googleTaskId && existing.googleTaskId) {
        toDelete.push(task.id)
      } else if (task.googleTaskId && !existing.googleTaskId) {
        toDelete.push(existing.id)
        seen.set(key, task)
      } else {
        // Both have or both lack googleTaskId — keep the one with lower id
        if (task.id > existing.id) {
          toDelete.push(task.id)
        } else {
          toDelete.push(existing.id)
          seen.set(key, task)
        }
      }
    } else {
      seen.set(key, task)
    }
  }

  if (toDelete.length > 0) {
    // 클라우드 삭제를 먼저 완료한 뒤 로컬 삭제 — 순서가 바뀌면 Firestore/Google에
    // 남아있는 데이터가 onSnapshot/fullSync를 통해 다시 로컬로 부활함
    const tasksToRemove = allTasks.filter(t => toDelete.includes(t.id))

    // Firestore 삭제: 개별 await로 실패한 것은 sync queue에 보관
    for (const task of tasksToRemove) {
      if (task.syncId) {
        try {
          await bridgeDeleteFromFirestore(task.syncId)
        } catch {
          // bridgeDeleteFromFirestore 실패 → sync queue에 직접 추가하여 나중에 재시도
          try {
            const { addToSyncQueueExternal } = await import('./firestoreSync')
            addToSyncQueueExternal({ type: 'delete-task', syncId: task.syncId, timestamp: new Date().toISOString() })
          } catch { /* firestoreSync import 자체 실패 — 무시 */ }
        }
      }
      // Google Tasks 삭제
      if (task.googleTaskId) {
        try {
          await deleteTaskFromGoogle(task.googleTaskId)
        } catch {
          console.warn(`[GoogleTasks] Google task 삭제 실패: ${task.googleTaskId}`)
        }
      }
    }

    await db.tasks.bulkDelete(toDelete)
    console.log(`[GoogleTasks] 중복 할일 ${toDelete.length}개 삭제 완료 (Firestore/Google 포함)`)
  }

  return toDelete.length
}

// ─── Clear All Google Tasks ─────────────────────────

export async function clearAllGoogleTasks(): Promise<{ deleted: number; errors: string[] }> {
  if (!getToken() || !window.gapi?.client?.tasks) {
    return { deleted: 0, errors: ['인증 토큰 없음 또는 Tasks API 미초기화'] }
  }

  const result = { deleted: 0, errors: [] as string[] }

  try {
    const allTasks = await fetchAllGoogleTasks()
    const toDelete = allTasks.filter(t => !t.deleted)
    console.log(`[GoogleTasks] Google Tasks 전체 삭제 시작: ${toDelete.length}개`)

    for (let i = 0; i < toDelete.length; i++) {
      try {
        await deleteGoogleTask(toDelete[i].id)
        result.deleted++
        // Rate limit
        if (i < toDelete.length - 1 && (i + 1) % 5 === 0) {
          await new Promise(r => setTimeout(r, 500))
        }
      } catch (err: any) {
        if (isQuotaError(err)) {
          console.warn(`[GoogleTasks] 할당량 초과 — ${result.deleted}개 삭제 완료, 나머지는 재시도 필요`)
          result.errors.push(`할당량 초과: ${result.deleted}개 삭제 후 중단`)
          break
        }
        // 404 is fine — already deleted
        if (err?.status !== 404 && err?.result?.error?.code !== 404) {
          const errMsg = err?.result?.error?.message || String(err)
          result.errors.push(`삭제 실패 (${toDelete[i].id}): ${errMsg}`)
        }
      }
    }

    // Clear googleTaskId from all local tasks + push to Firestore (#22)
    const localTasks = await db.tasks.toArray()
    for (const task of localTasks) {
      if (task.googleTaskId) {
        await db.tasks.update(task.id!, {
          googleTaskId: undefined,
          googleTaskListId: undefined,
          googleTaskUpdatedAt: undefined,
          updatedAt: new Date().toISOString(),
        })
        // #22: Firestore에도 googleTaskId 제거 push
        const updatedTask = await db.tasks.get(task.id!) as SavedTask
        if (updatedTask) bridgePushToFirestore(updatedTask).catch(console.error)
      }
    }

    console.log(`[GoogleTasks] Google Tasks 전체 삭제 완료: ${result.deleted}개`)
  } catch (err) {
    result.errors.push(`전체 삭제 실패: ${err instanceof Error ? err.message : String(err)}`)
    console.error('[GoogleTasks] clearAllGoogleTasks 실패:', err)
  }

  return result
}
